import React, { useEffect, useRef, useState } from 'react';
import { APPS, BHLogoMark } from '@buddieshr/components/brandLogos';

/**
 * The narrated product tour with its chapter chips underneath. Used on
 * /product-tour and in the home page hero's tour modal.
 *
 * The chapter times are measured from the recording, not from the tour's own
 * timeline: the tour holds its narration at every scene change (a few
 * hundred milliseconds, or the whole of the title card shown between two
 * apps), so the video runs longer than the narration track, and by a
 * different amount on every take. Every one of these is also a forced
 * keyframe in the encode, so a jump seeks exactly and instantly. An app's
 * chapter opens on its title card.
 *
 * The shipped file is cut by demo/recut.py in the webapp repo, which also
 * prints these times. While the tour holds the narration the recording's
 * audio is exact digital silence, which the narration itself never is, so
 * every hold can be found precisely and matched to its scene change. The
 * picture is the capture as recorded, with any unusually long load trimmed.
 * The audio is the original narration render (demo/voiceover.mp3), never
 * cut or edited, with silence added only where the tour held it, which is
 * always at the end of a line.
 *
 * Re-cutting the video means re-measuring these. The encode was:
 *   ffmpeg -i raw.mov -i narration.wav \
 *     -filter_complex "[0:v]fps=30,scale=1484:970:flags=lanczos,\
 *        select='<kept spans>',setpts=N/30/TB[v]" -map "[v]" -map 1:a \
 *     -c:v libx264 -crf 23 -preset slow -tune stillimage \
 *     -force_key_frames "<these timestamps>" \
 *     -c:a aac -b:a 128k -ac 2 -movflags +faststart out.mp4
 */
const TOUR_VIDEO = '/demo-product-tour.mp4';
export const TOUR_LENGTH = '4 minutes 5 seconds';
/** The same, rounded, for the play button's label. Update both together. */
export const TOUR_MINUTES = 4;

/**
 * `short` is what the chip carries, since a row of ten only reads at a glance
 * if each one is a single word. It matches the tour's own chapter bar.
 */
const CHAPTERS = [
  { at: 0, app: null, short: 'Intro', label: 'Why it exists' },
  { at: 15.6, app: 'alfy', short: 'Alfy', label: 'Alfy Matching' },
  { at: 51.53, app: 'billy', short: 'Billy', label: 'Billy Birthday' },
  { at: 64.3, app: 'clappy', short: 'Clappy', label: 'Clappy Kudos' },
  { at: 86.23, app: 'palmy', short: 'Palmy', label: 'Palmy Vacation' },
  { at: 116.17, app: 'pulsy', short: 'Pulsy', label: 'Pulsy Survey' },
  { at: 148.57, app: 'linky', short: 'Linky', label: 'Linky Directory' },
  { at: 167.87, app: 'stany', short: 'Stany', label: 'Stany Standup' },
  { at: 192.63, app: 'perf', short: 'SimplePerf', label: 'SimplePerf' },
  { at: 235.79, app: null, short: 'Wrap', label: 'Pricing and trial' },
];

/** Registry entry for a chapter's app, or null for the two that are not one. */
const appFor = (id) => (id ? APPS.find((a) => a.id === id) : null);

/** The chapter times carry hundredths so they land on their keyframe; the
 *  label does not need them. */
const mmss = (seconds) => {
  const whole = Math.round(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
};

/** The chapter the video is inside at `time`, as its `at`. */
const chapterAt = (time) => {
  let found = CHAPTERS[0].at;
  for (const chapter of CHAPTERS) {
    if (chapter.at <= time + 0.05) found = chapter.at;
  }
  return found;
};

/**
 * `autoPlay` is for the hero modal, which opens on a click: the video starts
 * right away and the still-frame play button is skipped.
 */
const TourPlayer = ({ autoPlay = false, frameStyle }) => {
  const videoRef = useRef(null);
  const [activeAt, setActiveAt] = useState(CHAPTERS[0].at);
  // Until the first play the frame is a still with a thin control bar, which
  // reads as a screenshot. The big play button over it is what says "video".
  const [started, setStarted] = useState(autoPlay);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    const onPlay = () => setStarted(true);
    video.addEventListener('play', onPlay);
    return () => video.removeEventListener('play', onPlay);
  }, []);

  // Highlight whichever chip the video is currently inside, so the row reads
  // as a position in the tour and not just ten shortcuts. timeupdate fires
  // about four times a second, which is plenty for a chapter boundary.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    const sync = () => setActiveAt(chapterAt(video.currentTime));
    video.addEventListener('timeupdate', sync);
    video.addEventListener('seeked', sync);
    return () => {
      video.removeEventListener('timeupdate', sync);
      video.removeEventListener('seeked', sync);
    };
  }, []);

  // Space plays and pauses the tour from anywhere on the page, the way it
  // does on a video site, instead of scrolling the page down. Left to the
  // browser in three places: when typing in a field, when some other button
  // or link has focus (Space presses those), and when the video itself has
  // focus, where its own controls already toggle it and a second toggle
  // would cancel the first.
  //
  // The chapter chips are the exception to the button rule. Clicking one
  // leaves it focused, so without this the next Space would press it again
  // and restart the chapter instead of pausing. They are controls for the
  // video, so Space controls the video; Enter still activates a chip.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.code !== 'Space' && event.key !== ' ') return;
      if (event.altKey || event.ctrlKey || event.metaKey) return;

      const video = videoRef.current;
      if (!video) return;

      const target = event.target;
      const tag = target && target.tagName ? target.tagName.toLowerCase() : '';
      const typing =
        tag === 'input' ||
        tag === 'textarea' ||
        tag === 'select' ||
        (target && target.isContentEditable);
      const isChapterChip = Boolean(
        target && target.dataset && target.dataset.chapterChip
      );
      const pressable = (tag === 'button' || tag === 'a') && !isChapterChip;
      if (typing || pressable || target === video) return;

      event.preventDefault();
      if (video.paused) {
        video.play().catch(() => {
          // A key press counts as a gesture, but a browser can still refuse.
        });
      } else {
        video.pause();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Seeking a <video> is a property assignment, so a chapter jump is instant
  // and keeps playing. Every chapter is a keyframe in the encode, so the
  // player does not have to decode forward to reach it.
  const jumpTo = (seconds) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = seconds;
    video.play().catch(() => {
      // Autoplay with sound needs a gesture; the click is one, but a browser
      // can still refuse. The seek has happened either way.
    });
  };

  return (
    <>
        {/* ── The tour ─────────────────────────────────────────────── */}
        <div
          style={{
            position: 'relative',
            overflow: 'hidden',
            background: 'var(--bh-ink)',
            /* The tour window is 1440x944, not 16:9. Reserving its real ratio
               keeps the player from pillarboxing and avoids a layout shift. */
            aspectRatio: '1484 / 970',
            ...frameStyle,
          }}
        >
          <video
            ref={videoRef}
            src={TOUR_VIDEO}
            // The native bar stays hidden until the first play, so the only
            // control on the still is the big button and nothing competes.
            controls={started}
            autoPlay={autoPlay}
            playsInline
            preload={autoPlay ? 'auto' : 'metadata'}
            style={{ width: '100%', height: '100%', display: 'block' }}
          >
            Your browser cannot play this video.
          </video>
          {!started && (
            <button
              type="button"
              className="tour-play"
              onClick={() => jumpTo(0)}
              aria-label={`Play the product tour (${TOUR_LENGTH})`}
            >
              <span className="tour-play-stack">
                <span className="tour-play-disc" aria-hidden="true">
                  <svg viewBox="0 0 24 24">
                    <path d="M8 5.5v13a1 1 0 0 0 1.5.86l10.5-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5z" />
                  </svg>
                </span>
                <span className="tour-play-label">
                  Watch the tour · {TOUR_MINUTES} min
                </span>
              </span>
            </button>
          )}
        </div>

        {/* ── Chapters ─────────────────────────────────────────────────
            A row of chips rather than a grid of cards, and directly under
            the player: these are controls for the video above them, so they
            read better as one strip the eye skims than as ten blocks that
            compete with it. The tour itself uses the same bar. */}
        <div
          style={{
            marginTop: 14,
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          {CHAPTERS.map((chapter) => {
            const app = appFor(chapter.app);
            const Logo = app ? app.Logo : null;
            const current = chapter.at === activeAt;
            return (
              <button
                key={chapter.at}
                type="button"
                data-chapter-chip="true"
                onClick={() => jumpTo(chapter.at)}
                title={`${chapter.label} · ${mmss(chapter.at)}`}
                aria-current={current ? 'true' : undefined}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 7,
                  height: 36,
                  padding: '0 14px 0 7px',
                  borderRadius: 999,
                  border: `1px solid ${
                    current ? 'var(--bh-sun-400)' : 'var(--bh-line)'
                  }`,
                  background: current
                    ? 'var(--bh-sun-200)'
                    : 'var(--bh-surface)',
                  color: current ? 'var(--bh-ink)' : 'var(--bh-ink-soft)',
                  // Set apart rather than as the `font` shorthand: `inherit` is
                  // not a valid family inside it, so the whole declaration is
                  // dropped and the weight silently goes back to 400.
                  fontFamily: 'inherit',
                  fontSize: 13.5,
                  fontWeight: 600,
                  lineHeight: 1,
                  cursor: 'pointer',
                  transition: 'background .18s ease, border-color .18s ease',
                }}
              >
                <span
                  style={{ flex: '0 0 22px', width: 22, height: 22 }}
                  aria-hidden="true"
                >
                  {Logo ? (
                    <Logo bg={app.bg} />
                  ) : (
                    /* The intro and the wrap are not an app, so they take the
                       house mark rather than borrowing someone's. */
                    <BHLogoMark size={22} />
                  )}
                </span>
                {chapter.short}
                {/* The time-code says, at a glance, that the chip is a place in
                    the video to jump to, not a tag. */}
                <span
                  style={{
                    fontWeight: 500,
                    fontVariantNumeric: 'tabular-nums',
                    color: current ? 'var(--bh-ink-soft)' : 'var(--bh-ink-mute)',
                  }}
                >
                  {mmss(chapter.at)}
                </span>
              </button>
            );
          })}
        </div>
    </>
  );
};

export default TourPlayer;
